import type {
  CallbackSlot,
  ClientMessage,
  MessageEntry,
  MessageHistoryEntry,
  MessageResolution,
  MessageVisibility,
} from "../types";

const HOUR = 3600000;
const DAY = 24 * HOUR;

type MockSeed = Pick<
  ClientMessage,
  "customer_name" | "phone_number" | "subject" | "body" | "category" | "urgency"
> &
  Partial<ClientMessage> & {
    /** How long ago the call happened. */
    ago: number;
    /** When the customer was promised an answer, relative to now (0: nothing promised). */
    due_in?: number;
  };

const seeds: MockSeed[] = [
  {
    ago: 26 * HOUR,
    due_in: -2 * HOUR,
    customer_name: "Sarah Johnson",
    phone_number: "+1 (415) 555-0132",
    subject: "Refund for a cancelled appointment",
    body: "Her appointment was **cancelled by the salon on the day** and she was charged the **$40 deposit** anyway. She wants to know whether it will be refunded, and when.\n\n**Called back the next day**: still no refund on her card. She would accept a credit on her next visit instead, if that is quicker.",
    tasks: [
      {
        id: "task_demo_1",
        label: "Check the deposit charged on the cancelled booking",
        done: true,
        done_at: Date.now() - 2 * HOUR,
        done_by: "demo_user",
      },
      {
        id: "task_demo_2",
        label: "Refund the deposit or offer a credit on her next visit",
        done: false,
        done_at: 0,
        done_by: "",
      },
      {
        id: "task_demo_3",
        label: "Tell her which one was chosen",
        done: false,
        done_at: 0,
        done_by: "",
      },
    ],
    session_ids: ["session_1", "session_9"],
    category: "billing",
    urgency: "high",
    source: "agent",
    suggested_reply:
      "The deposit will be refunded in full within 5 business days, on the card used for the booking.",
  },
  {
    ago: 3 * HOUR,
    due_in: 5 * HOUR,
    customer_name: "Michael Smith",
    phone_number: "+1 (212) 555-0187",
    subject: "Group booking for a wedding (6 people)",
    body: "Wants six people styled on Saturday morning, 12th, for a wedding. Asks whether the team can open an hour early and for a price for the whole group.",
    category: "quote",
    urgency: "normal",
    source: "agent",
    assigned_member_name: "Julie",
    assigned_member_id: "member_demo_julie",
  },
  {
    ago: 5 * HOUR,
    customer_name: "Emma Williams",
    phone_number: "+1 (646) 555-0144",
    subject: "Complaint about her last colour",
    body: "Unhappy with the colour done last week (brassy on the lengths). Wants to speak to the manager before booking a correction.",
    category: "complaint",
    urgency: "high",
    source: "review",
  },
  {
    ago: 8 * HOUR,
    due_in: 16 * HOUR,
    customer_name: "James Brown",
    phone_number: "+1 (312) 555-0199",
    subject: "Is the new keratin treatment safe during pregnancy?",
    body: "Asked whether the keratin smoothing treatment can be done while pregnant. The agent had no information and promised an answer by tomorrow.",
    category: "information",
    urgency: "normal",
    source: "agent",
    reply:
      "Our supplier advises against it during pregnancy. We can offer a formaldehyde-free gloss treatment instead.",
  },
  {
    ago: 30 * HOUR,
    due_in: -6 * HOUR,
    customer_name: "Olivia Jones",
    phone_number: "+1 (617) 555-0121",
    subject: "Gift card not received",
    body: "Bought a $100 gift card online on Monday and never received the e-mail. Wants it re-sent to another address.",
    category: "other",
    urgency: "normal",
    source: "agent",
  },
  {
    ago: 2 * DAY,
    customer_name: "William Garcia",
    phone_number: "+1 (305) 555-0175",
    subject: "Loyalty discount for a regular",
    body: "Comes every three weeks for ten years and asks whether a loyalty discount could apply to his next visits.",
    category: "commercial",
    urgency: "low",
    source: "review",
    status: "handled",
    resolution: "delegated",
    reply: "Yes: 10% off every visit from now on, it is already on your file.",
  },
  {
    ago: 3 * DAY,
    customer_name: "Sophia Miller",
    phone_number: "+1 (702) 555-0110",
    subject: "Wants to speak to Laura directly",
    body: "Insists on speaking to Laura about a personal matter, would not say more to the agent.",
    category: "callback",
    urgency: "normal",
    source: "agent",
    status: "handled",
    resolution: "handled_by_human",
    reply: "Laura called her back on Wednesday.",
  },
  {
    ago: 4 * DAY,
    customer_name: "Robert Davis",
    phone_number: "+1 (503) 555-0163",
    subject: "Hiring: sends his résumé",
    body: "Hairdresser looking for a job, wanted to know where to send his résumé.",
    category: "other",
    urgency: "low",
    source: "review",
    status: "handled",
    resolution: "dropped",
  },
];

function buildMockMessages(): ClientMessage[] {
  const now = Date.now();

  return seeds.map(({ ago, due_in, ...seed }, i) => {
    const createdAt = now - ago;
    const handled = seed.status === "handled";

    return {
      id: `message_${i + 1}`,
      client_id: "demo_client",
      session_id: `session_${i + 1}`,
      customer_id: `customer_${i + 1}`,
      agent_id: "agent_ai",
      language: "en",
      timezone: "America/New_York",
      assigned_member_id: "",
      assigned_member_name: "",
      due_at: due_in === undefined ? 0 : now + due_in,
      source: "agent",
      status: "pending",
      handled_at: handled ? createdAt + 4 * HOUR : 0,
      handled_by: handled ? "demo_user" : "",
      reply: "",
      suggested_reply: "",
      resolution: "",
      reminder_id: "",
      reply_delivered_at:
        seed.resolution === "delegated" ? createdAt + DAY : 0,
      created_at: createdAt,
      updated_at: createdAt,
      created_by: "agent_ai",
      updated_by: "agent_ai",
      is_deleted: false,
      ...seed,
    } as ClientMessage;
  });
}

/**
 * The demo inbox is state, not a constant: answering, closing and assigning a
 * request is the whole point of showing this page, so the mutations rewrite
 * these rows in memory and the next fetch reads them back. A reload starts over.
 */
let mockMessages = buildMockMessages();

function update(id: string, patch: Partial<ClientMessage>): ClientMessage {
  mockMessages = mockMessages.map((m) =>
    m.id === id ? { ...m, ...patch, updated_at: Date.now() } : m,
  );
  const message = mockMessages.find((m) => m.id === id);
  if (!message) throw new Error("Message not found");
  return message;
}

export function listMockMessages(options?: {
  status?: string;
  assignedMemberId?: string;
  customerId?: string;
}): { messages: ClientMessage[]; total: number } {
  const messages = mockMessages.filter(
    (m) =>
      (!options?.status || m.status === options.status) &&
      (!options?.assignedMemberId ||
        m.assigned_member_id === options.assignedMemberId) &&
      (!options?.customerId || m.customer_id === options.customerId),
  );
  return { messages, total: messages.length };
}

export function getMockMessage(id: string): { message: ClientMessage } {
  const message = mockMessages.find((m) => m.id === id);
  if (!message) throw new Error("Message not found");
  return { message };
}

/** Tomorrow at 10:00 local time, as if it came from the opening hours. */
export function getMockCallbackSlot(): CallbackSlot {
  const at = new Date();
  at.setDate(at.getDate() + 1);
  at.setHours(10, 0, 0, 0);
  return {
    at: at.getTime(),
    source: "opening_hours",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    enabled: true,
  };
}

/** Timelines are state too: a comment written in the demo is there on the next read. */
let mockEntries: MessageEntry[] = [];

const entryOf = (
  messageId: string,
  patch: Partial<MessageEntry> & Pick<MessageEntry, "kind" | "body">,
): MessageEntry => ({
  id: `entry_${mockEntries.length + 1}_${Date.now()}`,
  message_id: messageId,
  visibility: "private",
  author_type: "member",
  author_id: "demo_user",
  author_name: "Demo",
  session_id: "",
  event: "",
  delivered_at: 0,
  created_at: Date.now(),
  ...patch,
});

export function listMockEntries(id: string): { entries: MessageEntry[] } {
  const message = mockMessages.find((m) => m.id === id);
  const written = mockEntries.filter((e) => e.message_id === id);
  // A seeded answer is the first public line of its discussion.
  const seeded =
    message?.reply && !written.some((e) => e.visibility === "public")
      ? [
          entryOf(id, {
            id: `seed_${id}`,
            kind: "comment",
            visibility: "public",
            body: message.reply,
            created_at: message.handled_at || message.updated_at,
            delivered_at: message.reply_delivered_at,
          }),
        ]
      : [];
  return {
    entries: [...seeded, ...written].sort((a, b) => a.created_at - b.created_at),
  };
}

export function addMockEntry(
  id: string,
  data: { body: string; visibility: MessageVisibility },
): { message: ClientMessage; entries: MessageEntry[] } {
  mockEntries = [
    ...mockEntries,
    entryOf(id, { kind: "comment", visibility: data.visibility, body: data.body }),
  ];
  const message =
    data.visibility === "public"
      ? update(id, { reply: data.body, reply_delivered_at: 0 })
      : update(id, {});
  return { message, entries: listMockEntries(id).entries };
}

/** A seeded answer becomes a real line the first time somebody touches it. */
const materialise = (id: string) => {
  for (const entry of listMockEntries(id).entries) {
    if (!mockEntries.some((e) => e.id === entry.id)) {
      mockEntries = [...mockEntries, entry];
    }
  }
};

export function setMockEntryVisibility(
  id: string,
  entryId: string,
  visibility: MessageVisibility,
): { message: ClientMessage; entries: MessageEntry[] } {
  materialise(id);
  mockEntries = mockEntries.map((e) =>
    e.id === entryId && !e.removed_at
      ? { ...e, visibility, ...(visibility === "public" ? { delivered_at: 0 } : {}) }
      : e,
  );
  return syncMockReply(id);
}

export function deleteMockEntry(
  id: string,
  entryId: string,
): { message: ClientMessage; entries: MessageEntry[] } {
  materialise(id);
  mockEntries = mockEntries.map((e) =>
    e.id === entryId
      ? { ...e, body: "", removed_at: Date.now(), removed_by_name: "Demo" }
      : e,
  );
  return syncMockReply(id);
}

/** `reply` is what the customer is still owed: the public lines not yet told. */
function syncMockReply(id: string) {
  const owed = mockEntries
    .filter(
      (e) =>
        e.message_id === id &&
        e.kind === "comment" &&
        e.visibility === "public" &&
        !e.removed_at &&
        !e.delivered_at,
    )
    .map((e) => e.body)
    .join("\n\n");
  return {
    message: update(id, { reply: owed }),
    entries: listMockEntries(id).entries,
  };
}

export function getMockHistory(id: string): { history: MessageHistoryEntry[] } {
  const message = mockMessages.find((m) => m.id === id);
  if (!message) return { history: [] };
  const actor = { kind: "system" as const };
  return {
    history: [
      ...(message.status === "handled"
        ? [
            {
              operation: "UPDATE" as const,
              operation_timestamp: message.handled_at,
              changes: ["status" as const],
              values: { status: message.resolution || "handled" },
              actor: {
                kind: "user" as const,
                id: "demo_user",
                name: "Demo",
                avatar: null,
              },
            },
          ]
        : []),
      {
        operation: "INSERT" as const,
        operation_timestamp: message.created_at,
        changes: [],
        values: { status: "pending", urgency: message.urgency },
        actor,
      },
    ],
  };
}

export function handleMockMessage(
  id: string,
  data: { resolution: Exclude<MessageResolution, "">; reply?: string },
): { message: ClientMessage } {
  if (data.reply) {
    mockEntries = [
      ...mockEntries,
      entryOf(id, { kind: "comment", visibility: "public", body: data.reply }),
    ];
  }
  mockEntries = [
    ...mockEntries,
    entryOf(id, {
      kind: "event",
      author_type: "system",
      author_id: "",
      author_name: "",
      event: `closed:${data.resolution}`,
      body: "",
    }),
  ];
  return {
    message: update(id, {
      status: "handled",
      resolution: data.resolution,
      handled_at: Date.now(),
      handled_by: "demo_user",
      ...(data.reply ? { reply: data.reply, reply_delivered_at: 0 } : {}),
      ...(data.resolution === "delegated"
        ? { reminder_id: `reminder_demo_${id}` }
        : {}),
    }),
  };
}

export function assignMockMessage(
  id: string,
  memberId: string,
  memberName: string,
): { message: ClientMessage } {
  return {
    message: update(id, {
      assigned_member_id: memberId,
      assigned_member_name: memberId ? memberName : "",
    }),
  };
}

export function triageMockMessage(
  id: string,
  change: { urgency?: ClientMessage["urgency"]; category?: string; due_at?: number },
): { message: ClientMessage } {
  return {
    message: update(id, {
      ...change,
      ...(change.due_at ? { due_source: "manual" as const } : {}),
    }),
  };
}

export function setMockTask(
  id: string,
  taskId: string,
  done: boolean,
): { message: ClientMessage } {
  const message = mockMessages.find((m) => m.id === id);
  return {
    message: update(id, {
      tasks: (message?.tasks || []).map((task) =>
        task.id === taskId
          ? {
              ...task,
              done,
              done_at: done ? Date.now() : 0,
              done_by: done ? "demo_user" : "",
            }
          : task,
      ),
    }),
  };
}
